// TintiCloud — Camada de acesso a dados (data-access layer).
// Funções já migradas usam o Control Plane real (src/lib/apiClient.js).
// As demais continuam em mocks com atraso simulado até serem migradas.
// A UI NÃO deve importar mocks (nem o backend) diretamente — sempre passar por aqui.
import * as M from "@/mocks";
import { http } from "@/lib/apiClient";
import { npmSyncFailure } from "@/data/npmState";

const DELAY = 380;
const delay = (data, ms = DELAY) => new Promise((res) => setTimeout(() => res(data), ms));

// ---- Adaptadores: formato real do backend -> formato que a UI já espera ----
// Campos que o backend ainda não coleta (load[], net.in/out, uptime "Xd Yh"...)
// recebem um placeholder neutro — nunca um valor inventado que pareça real.

const CONNECTION_TO_MOCK_STATUS = { online: "online", offline: "offline", never_connected: "standby", incompatible: "error" };

function humanizeUptime(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  return `${d}d ${h}h`;
}

function adaptServer(s, containerCount) {
  return {
    id: s.id,
    hostname: s.hostname,
    label: s.label || s.hostname,
    ip: s.ip || "—",
    region: s.region || "—",
    provider: s.provider || "—",
    os: s.os || "—",
    status: CONNECTION_TO_MOCK_STATUS[s.connection_status] || "standby",
    uptime: humanizeUptime(s.uptime_seconds),
    cpuPct: s.cpu_pct,
    cpuCores: s.cpu_cores ?? "—",
    ramUsed: s.ram_used_mb != null ? +(s.ram_used_mb / 1024).toFixed(1) : null,
    ramTotal: s.ram_total_mb != null ? +(s.ram_total_mb / 1024).toFixed(1) : null,
    diskUsed: s.disk_used_gb,
    diskTotal: s.disk_total_gb,
    containers: containerCount ?? 0,
    load: [],
    net: { in: "—", out: "—" },
    agentVersion: s.agent_version || null,
    lastSeen: s.last_seen_at, protocolVersion: s.protocol_version, capabilities: s.capabilities || [],
  };
}

function adaptContainer(c, serverId, projectNameById = {}) {
  return {
    id: c.id,
    name: c.name,
    project: c.project_id ? (projectNameById[c.project_id] || "—") : "—",
    projectId: c.project_id || null,
    server: serverId,
    image: c.image || "—",
    // Status REAL efetivo (Docker via Agent, "unknown" se o Server/Agent
    // está offline, "missing" se o Agent parou de reportar este container) —
    // nunca o `state` bruto sozinho, que pode estar desatualizado.
    status: c.effective_status || c.state,
    rawState: c.state,
    cpu: c.cpu_pct ?? 0,
    memUsed: c.mem_used_mb ?? 0,
    memTotal: c.mem_limit_mb ?? 0,
    ports: c.ports || "—",
    uptime: "—",
    adoptionState: c.adoption_state,
    missingSince: c.missing_since || null,
    siteId: c.site_id || null,
    siteServiceRole: c.site_service_role || null,
  };
}

async function fetchAllServersWithContainers() {
  const servers = await http.get("/servers");
  const withContainers = await Promise.all(
    servers.map(async (s) => {
      const containers = await http.get(`/servers/${s.id}/containers`);
      return { server: s, containers };
    }),
  );
  return withContainers;
}

async function fetchAcrossServers(resourcePath) {
  const servers = await http.get("/servers");
  const rows = await Promise.all(
    servers.map(async (s) => {
      const items = await http.get(`/servers/${s.id}/${resourcePath}`);
      return { server: s, items };
    }),
  );
  return rows;
}

function adaptNetwork(n, serverHostname) {
  return { name: n.name, server: serverHostname, driver: n.driver || "—", subnet: n.subnet || "—", containers: "—", status: n.adoption_state };
}

function adaptVolume(v, serverHostname) {
  return { name: v.name, server: serverHostname, driver: v.driver || "—", size: "—", used: 0, status: v.adoption_state };
}

function adaptClient(c, projectCount) {
  return { id: c.id, name: c.name, contact: c.contact_email, plan: c.plan, sites: projectCount ?? 0, services: "—", status: c.status };
}

function adaptUser(u) {
  return { id: u.id, name: u.name, email: u.email, role: u.roles?.[0] || "—", status: u.is_active ? "online" : "offline", last: "—", twofa: u.twofa_enabled };
}

function adaptTicket(t, clientNameById) {
  return {
    id: t.id,
    subject: t.subject,
    client: clientNameById[t.client_id] || "—",
    category: t.category,
    priority: t.priority,
    status: t.status,
    service: "—",
    updated: new Date(t.updated_at).toLocaleString("pt-BR"),
    created: new Date(t.created_at).toLocaleString("pt-BR"),
    messages: (t.messages || []).map((m) => ({
      from: m.from_role,
      author: m.from_role === "client" ? "Cliente" : "TintiCloud",
      at: new Date(m.created_at).toLocaleString("pt-BR"),
      text: m.body,
    })),
  };
}

export const api = {
  // Dashboard — um único snapshot RBAC-filtered para manter cards, listas e
  // totais consistentes. O backend não dispara discovery nem mutações.
  getDashboardSummary: async () => {
    const summary = await http.get('/dashboard/summary');
    return {
      ...summary,
      servers: (summary.servers || []).map((server) => adaptServer(server, server.container_count)),
      domains: (summary.domains || []).map((domain) => ({
        ...domain,
        name: domain.domain,
        project: domain.project || '—',
        proxy: domain.proxy,
        sslStatus: domain.ssl_status,
        expires: domain.ssl_expires_at || '—',
      })),
      containers: summary.containers || { total: 0, running: 0, stopped: 0, paused: 0, error: 0 },
    };
  },
  getDashboardStats: async () => (await api.getDashboardSummary()).stats,
  getInfraUsage: () => api.getServerUsage(),
  getInfraStatus: async () => {const servers=await http.get('/servers');const online=servers.filter(s=>s.connection_status==='online').length;return {ok:servers.length>0&&online===servers.length,label:`Agents online: ${online}/${servers.length}`};},

  // Servidores — MIGRADO para o Control Plane real.
  getServers: async () => {
    const rows = await fetchAllServersWithContainers();
    return rows.map(({ server, containers }) => adaptServer(server, containers.length));
  },
  createServer: (body) => http.post("/servers", body),
  getServer: async (id) => {
    const [server, containers] = await Promise.all([
      http.get(`/servers/${id}`),
      http.get(`/servers/${id}/containers`),
    ]);
    if (!server) return null;
    return adaptServer(server, containers.length);
  },
  getServerExtras: async (id) => {
    const [networks,volumes]=await Promise.all([http.get(`/servers/${id}/networks`),http.get(`/servers/${id}/volumes`)]);
    return {networks:networks.map(n=>({...n,scope:'—',containers:'—'})),volumes:volumes.map(v=>({...v,mount:v.mountpoint,size:v.size_bytes==null?'—':`${v.size_bytes} bytes`,used:null})),backups:[]};
  },
  getServerUsage: async () => ({"1h":[],"24h":[],"7d":[],"30d":[]}), // Histórico não coletado.

  // Containers — MIGRADO (lista agregada de todos os servidores reais).
  getContainers: async () => {
    const [rows, projects] = await Promise.all([fetchAllServersWithContainers(), http.get("/projects").catch(() => [])]);
    const projectNameById = Object.fromEntries(projects.map((p) => [p.id, p.name]));
    return rows.flatMap(({ server, containers }) => containers.map((c) => adaptContainer(c, server.id, projectNameById)));
  },
  adoptContainer: (serverId, containerId, body) => http.post(`/servers/${serverId}/containers/${containerId}/adopt`, body),
  adoptContainersBatch: (serverId, body) => http.post(`/servers/${serverId}/containers/adopt-batch`, body),
  startContainer: (serverId, containerId) => http.post(`/servers/${serverId}/containers/${containerId}/start`),
  stopContainer: (serverId, containerId) => http.post(`/servers/${serverId}/containers/${containerId}/stop`),
  restartContainer: (serverId, containerId) => http.post(`/servers/${serverId}/containers/${containerId}/restart`),
  getJob: (id) => http.get(`/jobs/${id}`),
  getJobs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return http.get(`/jobs${qs ? `?${qs}` : ""}`);
  },
  getSites: async () => (await http.get("/sites")).map((s) => ({ id: s.id, name: s.name, primaryDomain: s.primary_domain, environmentId: s.environment_id })),
  getContainerSummary: async () => (await api.getDashboardSummary()).containers,

  // Projetos — MIGRADO (lista). Domínios/branch/serviços ainda não existem
  // como dados reais (Repository/Domain/Deployment sem endpoints ainda).
  getProjects: async () => {
    const [projects, clients, servers] = await Promise.all([
      http.get("/projects"), http.get("/clients"), http.get("/servers").catch(e=>{if(e.status===403)return [];throw e;}),
    ]);
    const clientNameById = Object.fromEntries(clients.map((c) => [c.id, c.name]));
    const serverById = Object.fromEntries(servers.map((s) => [s.id, s]));
    const withEnvs = await Promise.all(projects.map((p) => http.get(`/projects/${p.id}/environments`)));
    return projects.map((p, i) => {
      const env = withEnvs[i]?.[0];
      const server = env?.server_id ? serverById[env.server_id] : null;
      return {
        id: p.id, name: p.name, client: clientNameById[p.client_id] || "—", clientId: p.client_id,
        status: p.status, server: server?.hostname || "—", stack: p.stack || "—", framework: p.framework || "—",
        env: env?.name || "—", domains: ["—"], services: [], branch: "—", commit: "—", lastDeploy: "—",
      };
    });
  },
  getProject: async (id) => {
    const projects = await api.getProjects();
    return projects.find((p) => p.id === id) || null;
  },
  getProjectServices: (id) => delay(M.projectServices[id] || null),

  // Domains — Control Plane real. A missing relationship stays unknown.
  getDomains: async () => (await http.get('/domains')).map((d) => ({
    id: d.id, domain: d.domain, name: d.domain, project: d.project || '—', projectId: d.project_id,
    client: d.client || '—', server: d.server || '—', proxy: d.proxy_status || 'Unknown',
    dns: d.cloudflare === 'not_configured' ? 'Não configurado' : d.cloudflare,
    ssl: d.ssl_status === 'assigned' ? 'Certificado atribuído' : 'Unknown', sslStatus: d.ssl_status,
    expires: d.ssl_expires_at || '—', status: d.proxy_status === 'enabled' ? 'healthy' : 'unknown',
    adoptionState: d.adoption_state, lastSync: d.last_sync_at, missingSince: d.missing_since,
  })),
  getDomainsAttention: async () => (await api.getDashboardSummary()).alerts,
  getDomain: async (domain) => (await http.get('/domains')).find((d) => d.domain === domain) || null,
  // Project cockpit / ownership (dados derivados do ownership real — nada copiado).
  getProjectOverview: (id) => http.get(`/projects/${id}/overview`),
  getProjectAdoptionCandidates: (id) => http.get(`/projects/${id}/adoption-candidates`),
  adoptProjectServices: (id, containerIds) => http.post(`/projects/${id}/services/adopt`, { container_ids: containerIds }),
  // Client: dados comerciais, Projects e membros do Portal (membership ≠ contact_email).
  getClientOverview: (id) => http.get(`/clients/${id}/overview`),
  inviteClientMember: (id, body) => http.post(`/clients/${id}/members`, body),
  removeClientMember: (id, userId) => http.delete(`/clients/${id}/members/${userId}`),
  ensureInternalClient: () => http.post('/clients/internal'),
  // Portal: mesma fonte de verdade, visão sanitizada.
  getPortalProjects: () => http.get('/portal/projects'),
  getPortalProject: (id) => http.get(`/portal/projects/${id}`),
  // Inventário / reconciliação (admin).
  getInventory: () => http.get('/inventory'),
  deriveOwnership: (apply = false) => http.post(`/inventory/derive-ownership${apply ? '?apply=true' : ''}`),
  // Stacks: classificação (não é ownership), bootstrap de Project interno e atribuição.
  classifyStack: (key, classification, needsReview = false, note = '') => http.post('/inventory/stacks/classify', { ...key, classification, needs_review: needsReview, note }),
  stackBootstrapPlan: (key) => http.post('/inventory/stacks/bootstrap-plan', key),
  bootstrapStack: (key) => http.post('/inventory/stacks/bootstrap', key),
  assignStack: (key, projectId) => http.post('/inventory/stacks/assign', { ...key, project_id: projectId }),
  // Owner interno estrutural (flag), Project → Client e acesso interno (Project members).
  setClientInternal: (id, isInternal) => http.patch(`/clients/${id}/internal`, { is_internal: isInternal }),
  transferProjectClient: (projectId, clientId) => http.patch(`/projects/${projectId}/client`, { client_id: clientId }),
  getProjectMembers: (id) => http.get(`/projects/${id}/members`),
  addProjectMember: (id, body) => http.post(`/projects/${id}/members`, body),
  removeProjectMember: (id, userId) => http.delete(`/projects/${id}/members/${userId}`),
  listClientsRaw: () => http.get('/clients'),
  listProjectsRaw: () => http.get('/projects'),
  listServersRaw: () => http.get('/servers'),
  // Environment → Server (metadado; sem confirm retorna preview; nunca adota recursos).
  changeEnvironmentServer: (projectId, envId, body) => http.patch(`/projects/${projectId}/environments/${envId}/server`, body),
  getServersUsage: () => http.get('/inventory/servers'),
  // Site: escolher domínio principal (NEEDS REVIEW) — local, sem NPM/DNS até Apply explícito.
  setSitePrimary: (siteId, hostname) => http.post(`/sites/${siteId}/primary`, { hostname }),
  // Site: domínio principal + aliases (mesmo Proxy Host gerenciado).
  getSiteDomains: (siteId) => http.get(`/sites/${siteId}/domains`),
  addSiteAlias: (siteId, hostname) => http.post(`/sites/${siteId}/aliases`, { hostname }),
  removeSiteAlias: (siteId, domainId) => http.delete(`/sites/${siteId}/aliases/${domainId}`),
  applySiteDomains: (siteId, acceptLetsEncryptTerms = false) => http.post(`/sites/${siteId}/domains/apply`, { accept_letsencrypt_terms: acceptLetsEncryptTerms }),

  // Git / Deployments — Control Plane real. A ausência de dados permanece vazia.
  getRepositories: async () => (await http.get('/repositories')).map((r) => ({
    id: r.id, name: r.name, provider: r.provider, repositoryUrl: r.repository_url, branch: r.default_branch,
    project: r.project_name || '—', private: r.private, enabled: r.enabled,
    credentialConfigured: r.credential_configured, status: r.enabled ? 'online' : 'offline',
  })),
  createRepository: (body) => http.post('/repositories', body),
  updateRepository: (id, body) => http.patch(`/repositories/${id}`, body),
  archiveRepository: (id) => http.post(`/repositories/${id}/archive`),
  testRepositoryConnection: (id, branch) => http.post(`/repositories/${id}/test-connection${branch ? `?branch=${encodeURIComponent(branch)}` : ''}`),
  getDeployments: async () => (await http.get('/deployments')).map((d) => ({
    id: d.id, project: d.project_name || '—', env: d.environment_name || '—', repository: d.repository_name || '—',
    branch: d.branch || '—', commit: d.commit_sha || '—', author: d.requested_by_name || '—',
    duration: d.duration_seconds == null ? '—' : `${d.duration_seconds}s`, status: d.status,
    date: d.created_at ? new Date(d.created_at).toLocaleString('pt-BR') : '—', server: d.server_hostname || '—',
    error: d.error || null, jobId: d.job_id, raw: d,
  })),
  createDeployment: (body) => http.post('/deployments', body),
  rollbackDeployment: (id) => http.post(`/deployments/${id}/rollback`),

  // Logs
  getLogs: async () => (await http.get('/audit-logs')).map(l=>({
    id:l.id, ts:new Date(l.created_at).toLocaleString('pt-BR'), timestamp:l.created_at,
    level:l.result==='success'?'INFO':'ERROR', source:'audit', server:l.server_id||'—',
    actor:l.actor_name || 'Sistema', action:l.action, resourceType:l.resource_type,
    resourceId:l.resource_id, scope:l.server_id ? `server:${l.server_id}` : '—',
    result:l.result, meta:l.meta || {}, msg:`${l.action} · ${l.result} · ${l.resource_type} ${l.resource_id}`,
  })),

  // Integrações
  getIntegrations: () => delay(M.integrations),

  // Clientes / Portal / Usuários — Clientes e Usuários MIGRADOS.
  getClients: async () => {
    const [clients, projects] = await Promise.all([http.get("/clients"), http.get("/projects")]);
    return clients.map((c) => adaptClient(c, projects.filter((p) => p.client_id === c.id).length));
  },
  createClient: (body) => http.post("/clients", body),
  createProject: (body) => http.post("/projects", body),
  getClientPortal: () => http.get("/portal"),
  getUsers: async () => (await http.get("/users")).map(adaptUser),

  // Vault — MIGRADO.
  getSecrets: () => delay(M.secrets),
  getVaultProjects: async () => {
    const [projects, clients] = await Promise.all([http.get("/projects"), http.get("/clients")]);
    const clientNameById = Object.fromEntries(clients.map((c) => [c.id, c.name]));
    const counts = await Promise.all(projects.map((p) => http.get(`/projects/${p.id}/secrets`)));
    return projects.map((p, i) => ({ id: p.id, name: p.name, client: clientNameById[p.client_id] || "—", count: counts[i].length }));
  },
  getProjectSecrets: async (id) => {
    const [envs, secrets] = await Promise.all([
      http.get(`/projects/${id}/environments`),
      http.get(`/projects/${id}/secrets`),
    ]);
    const envNameById = Object.fromEntries(envs.map((e) => [e.id, e.name.charAt(0).toUpperCase() + e.name.slice(1)]));
    const grouped = { Production: [], Staging: [], Development: [] };
    for (const s of secrets) {
      const envName = envNameById[s.environment_id];
      const bucket = grouped[envName] || (grouped[envName] = []);
      bucket.push({ id: s.id, name: s.name, versions: s.version, updated: new Date(s.updated_at).toLocaleString("pt-BR"), by: "—" });
    }
    return grouped;
  },
  getPlatformSecrets: async () => {
    const rows = await http.get("/platform-secrets");
    return rows.map((r) => ({ id: r.id, name: r.name, category: r.category || "—", versions: r.version, updated: new Date(r.updated_at).toLocaleString("pt-BR"), by: "—" }));
  },

  // WordPress
  getWordpressSites: () => delay(M.wordpressSites),
  getImportMethods: () => delay(M.importMethods),

  // Infra diversa
  // Estado (Configurado/Conectado/erro/última sync) vem do backend; a lista
  // de hosts é o espelho local. `syncServerIds` dispara discovery (somente
  // leitura no NPM) apenas para esses servidores.
  getNpm: async (syncServerIds = []) => {
    const servers = await http.get('/servers');
    const rows = await Promise.all(servers.map(async (server) => {
      const configPath = `/servers/${server.id}/npm/configuration`;
      let config = await http.get(configPath);
      if (!config.configured || !config.enabled) {
        return { server, config, hosts: [], syncError: null };
      }
      let hosts = [];
      let syncError = null;
      try {
        hosts = syncServerIds.includes(server.id)
          ? await http.post(`/servers/${server.id}/npm/sync`)
          : await http.get(`/servers/${server.id}/npm/proxy-hosts`);
      } catch (e) {
        if (![400, 404, 409, 412, 429, 502, 504].includes(e.status)) throw e;
        syncError = npmSyncFailure(e);
        hosts = await http.get(`/servers/${server.id}/npm/proxy-hosts`).catch(() => []);
      }
      if (syncServerIds.includes(server.id)) config = await http.get(configPath);
      return { server, config, hosts, syncError };
    }));
    return { rows, hosts: rows.flatMap((r) => r.hosts.map((host) => ({ ...host, server: r.server.hostname, serverId: r.server.id }))) };
  },
  // Cria/substitui a credencial e valida via POST /api/tokens; senha vazia mantém a armazenada.
  configureNpm: (serverId, body) => http.post(`/servers/${serverId}/npm/credentials`, body),
  getNpmContainers: (serverId) => http.get(`/servers/${serverId}/containers`),
  createNpmProxyHost: (serverId, body) => http.post(`/servers/${serverId}/npm/proxy-hosts`, body),
  updateNpmProxyHost: (serverId, hostId, body) => http.patch(`/servers/${serverId}/npm/proxy-hosts/${hostId}`, body),
  reconcileNpmProxyHost: (serverId, hostId) => http.post(`/servers/${serverId}/npm/proxy-hosts/${hostId}/reconcile`),
  // Let's Encrypt (reutiliza certificado válido; só emite se necessário) + opções SSL, apenas hosts gerenciados.
  configureNpmProxyHostSsl: (serverId, hostId, body) => http.post(`/servers/${serverId}/npm/proxy-hosts/${hostId}/ssl`, body),
  getCloudflare: async () => {
    const configuration = await http.get('/cloudflare/configuration');
    if (!configuration.configured || !configuration.enabled) return { configuration, zones: [] };
    try {
      const zones = await http.get('/cloudflare/zones');
      return { configuration: { ...configuration, state: 'configured' }, zones };
    } catch (error) {
      if (error.status === 401) return { configuration: { ...configuration, state: 'authentication_error' }, zones: [], error: error.detail || 'Token inválido' };
      if (error.status === 403) return { configuration: { ...configuration, state: 'permission_error' }, zones: [], error: error.detail || 'Token válido, mas sem Zone:Read' };
      if (error.status === 502 || error.status === 504) return { configuration: { ...configuration, state: 'unavailable' }, zones: [], error: 'Cloudflare indisponível' };
      throw error;
    }
  },
  configureCloudflare: (token) => http.post('/cloudflare/credentials', { token, enabled: true }),
  testCloudflare: () => http.post('/cloudflare/test'),
  syncCloudflare: () => http.post('/cloudflare/sync'),
  getCloudflareZone: async (id) => {
    const zones = await http.get('/cloudflare/zones');
    const zone = zones.find((row) => row.id === id) || null;
    const records = zone ? await http.get(`/cloudflare/zones/${id}/records`) : [];
    return { zone, records };
  },
  createDnsRecord: (zoneId, body) => http.post(`/cloudflare/zones/${zoneId}/records`, body),
  updateDnsRecord: (zoneId, recordId, body) => http.patch(`/cloudflare/zones/${zoneId}/records/${recordId}`, body),
  deleteDnsRecord: (zoneId, recordId) => http.delete(`/cloudflare/zones/${zoneId}/records/${recordId}`),
  // Não existe model/provider de databases no Control Plane ainda. Nunca
  // retornar os dados da camada de mocks como se fossem infraestrutura real.
  getDatabases: async () => [],
  getMail: () => delay({ config: M.mailConfig, mailboxes: M.mailboxes }),
  // Email Management — MIGRADO (domínios/caixas Stalwart reais).
  getEmailDomains: () => http.get("/email-domains"),
  getEmailDomain: (id) => http.get(`/email-domains/${id}`),
  createEmailDomain: (body) => http.post("/email-domains", body),
  createMailbox: (domainId, body) => http.post(`/email-domains/${domainId}/mailboxes`, body),
  getEmailClientConfig: (domainId, mailboxId) => http.get(`/email-domains/${domainId}/mailboxes/${mailboxId}/client-config`),
  updateMailbox: (domainId, mailboxId, body) => http.patch(`/email-domains/${domainId}/mailboxes/${mailboxId}`, body),
  resetMailboxPassword: (domainId, mailboxId, body) => http.post(`/email-domains/${domainId}/mailboxes/${mailboxId}/password`, body),
  addMailboxAlias: (domainId, mailboxId, body) => http.post(`/email-domains/${domainId}/mailboxes/${mailboxId}/aliases`, body),
  removeMailboxAlias: (domainId, mailboxId, alias) => http.delete(`/email-domains/${domainId}/mailboxes/${mailboxId}/aliases/${encodeURIComponent(alias)}`),
  deleteMailbox: (domainId, mailboxId) => http.delete(`/email-domains/${domainId}/mailboxes/${mailboxId}`),
  verifyEmailDns: (domainId) => http.post(`/email-domains/${domainId}/dns/verify`),
  // Networks/Volumes — MIGRADO (discovery real, agregado entre servidores).
  getNetworks: async () => {
    const rows = await fetchAcrossServers("networks");
    return rows.flatMap(({ server, items }) => items.map((n) => adaptNetwork(n, server.hostname)));
  },
  getVolumes: async () => {
    const rows = await fetchAcrossServers("volumes");
    return rows.flatMap(({ server, items }) => items.map((v) => adaptVolume(v, server.hostname)));
  },
  getBackups: () => delay(M.backups),

  // Notificações
  getNotifications: () => delay(M.notifications),
  getCurrentUser: () => http.get("/auth/me"), // não usado por nenhuma página hoje; já aponta para o backend real

  // Portal do Cliente
  getClientSite: () => http.get("/portal").then(p => p.sites),
  getClientServices: () => http.get("/portal").then(p => p.projects),

  // Tickets — MIGRADO. Cliente do Portal não tem `clients.read` — nome do
  // cliente degrada para "—" nesse caso em vez de quebrar a lista de tickets.
  getTickets: async () => {
    const [tickets, clients] = await Promise.all([
      http.get("/tickets"),
      http.get("/clients").catch((e) => (e.status === 403 ? [] : Promise.reject(e))),
    ]);
    const clientNameById = Object.fromEntries(clients.map((c) => [c.id, c.name]));
    return tickets.map((t) => adaptTicket(t, clientNameById));
  },
  getTicket: async (id) => {
    const [ticket, clients] = await Promise.all([
      http.get(`/tickets/${id}`).catch(() => null),
      http.get("/clients").catch((e) => (e.status === 403 ? [] : Promise.reject(e))),
    ]);
    if (!ticket) return null;
    const clientNameById = Object.fromEntries(clients.map((c) => [c.id, c.name]));
    return adaptTicket(ticket, clientNameById);
  },
};

export const IS_DEMO = true;
